import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import ReceiptIcon from "@material-ui/icons/Receipt";
import PermMediaIcon from "@material-ui/icons/PermMedia";
import HistoryIcon from "@material-ui/icons/History";
import { useHistory, useLocation } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import { getPortalCustomerById, getPortalCustomers, patchPortalCustomerSubscription, syncPortalCustomerStripeBilling } from "../../../../api/user";
import { getPlansAdmin } from "../../../../api/plan";
import { getEscapeRoomsByUser } from "../../../../api/escape-room";
import { getUserMediaByUserId } from "../../../../api/files";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { SERVER_URL } from "../../../../utils";
import { shallowEqual, useSelector } from "react-redux";

function formatDate(iso) {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleString("es-ES", {
			dateStyle: "short",
			timeStyle: "short",
		});
	} catch (e) {
		return "—";
	}
}

/** Mismos valores que valida el backend en `PORTAL_ROLES`. */
const PORTAL_SUB_EDIT_ROLES = ["usuario", "usuario pro", "usuario premium", "usuario manual"];

function toDatetimeLocalValue(iso) {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return "";
		const pad = (n) => String(n).padStart(2, "0");
		return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
	} catch (e) {
		return "";
	}
}

function resolveBillingSummary(user) {
	if (!user) {
		return { provider: "—", isStripe: false, lastAt: null, nextAt: null, status: "", subId: "" };
	}
	const isStripe =
		String(user.paymentProvider || "").toLowerCase() === "stripe" ||
		Boolean(String(user.stripeSubscriptionId || "").trim());
	return {
		provider: isStripe ? "Stripe" : "Redsys",
		isStripe,
		lastAt: isStripe ? user.billingLastChargeAt : user.redsysLastChargeAt,
		nextAt: isStripe ? user.billingNextChargeAt : user.redsysNextChargeAt,
		status: isStripe ? user.billingLastChargeStatus : user.redsysLastChargeStatus,
		subId: String(user.stripeSubscriptionId || "").trim(),
	};
}

function DetailRow({ label, value }) {
	return (
		<div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, marginBottom: 8 }}>
			<Typography variant="body2" color="textSecondary">
				{label}
			</Typography>
			<Typography variant="body2" style={{ wordBreak: "break-word" }}>
				{value === undefined || value === null || value === "" ? "—" : String(value)}
			</Typography>
		</div>
	);
}

function formatBytes(n) {
  const b = Number(n || 0)
  if (!Number.isFinite(b) || b <= 0) return "0 B"
  const units = ["B","KB","MB","GB","TB"]
  let i = 0
  let v = b
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1 }
  const d = i >= 2 ? 1 : 0
  return `${v.toFixed(d)} ${units[i]}`
}

function resolveUploadedSrc(rawPath) {
	let p = String(rawPath || "");
	while (p.startsWith("/")) p = p.slice(1);
	if (!p) return "";
	if (/^https?:\/\//i.test(p)) return p;
	const base = String(SERVER_URL || "").replace(/\/$/, "");
	return `${base}/${p}`;
}

function escapeRoomPreviewUrl(er) {
	const pages = Array.isArray(er?.pages) ? er.pages : [];
	for (const page of pages) {
		const bg = page?.backgroundImageUrl;
		if (bg) return resolveUploadedSrc(bg);
		const els = Array.isArray(page?.elements) ? page.elements : [];
		for (const el of els) {
			if (el?.type === "image" && el?.content?.src) return resolveUploadedSrc(el.content.src);
			if (el?.type === "video" && el?.content?.src) return resolveUploadedSrc(el.content.src);
		}
	}
	return "";
}

function formatPlanPriceParen(plan) {
	if (!plan) return "—";
	const cents = parseInt(String(plan.amountCents ?? "0"), 10);
	if (Number.isFinite(cents) && cents > 0) {
		const euros = (cents / 100).toFixed(2).replace(".", ",");
		const suf = String(plan.priceSuffix || "").trim();
		return suf ? `${euros} €${suf}` : `${euros} €`;
	}
	const price = String(plan.price || "").trim();
	const suf = String(plan.priceSuffix || "").trim();
	if (!price) return "—";
	if (price === "0" || /^0[,.]0/.test(price) || price.toLowerCase() === "gratis") return "Gratis";
	return suf ? `${price}${suf}` : price;
}

function SectionCard({ title, icon, children, right }) {
	const iconWrap = (
		<div
			style={{
				width: 40,
				height: 40,
				borderRadius: 12,
				background: "linear-gradient(135deg, #e0f2fe, #eef2ff)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "#1d4ed8",
				flexShrink: 0,
			}}
		>
			{icon}
		</div>
	);
	return (
		<div
			style={{
				border: "1px solid #e5e7eb",
				borderRadius: 18,
				padding: 16,
				background: "#fff",
				height: "100%",
				minHeight: 380,
				display: "flex",
				flexDirection: "column",
				boxSizing: "border-box",
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 10,
					marginBottom: 12,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
					{iconWrap}
					<div style={{ fontWeight: 950, color: "#0f172a", fontSize: "1.05rem" }}>{title}</div>
				</div>
				{right ? (
					<div style={{ fontSize: 12, fontWeight: 900, color: "#64748b", flexShrink: 0 }}>{right}</div>
				) : null}
			</div>
			<div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{children}</div>
		</div>
	);
}

export default function PortalCustomersPage() {
	const history = useHistory();
	const location = useLocation();
	const authUser = useSelector((s) => s.authentication?.user, shallowEqual);
	const isAdmin = String(authUser?.role || "").toLowerCase() === "admin";
	const publicBase = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");

	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [plans, setPlans] = useState([]);
	const [detailOpen, setDetailOpen] = useState(false);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detailUser, setDetailUser] = useState(null);
	const [detailEscapeRooms, setDetailEscapeRooms] = useState([]);
	const [detailMedia, setDetailMedia] = useState(null);
	const [subRoleDraft, setSubRoleDraft] = useState("");
	const [subNextDraft, setSubNextDraft] = useState("");
	const [subNextDirty, setSubNextDirty] = useState(false);
	const [subSaving, setSubSaving] = useState(false);
	const [subSyncing, setSubSyncing] = useState(false);
	const [subAddonRooms, setSubAddonRooms] = useState("0");
	const [subAddonPlayers, setSubAddonPlayers] = useState("0");
	const [subAddonStorageMb, setSubAddonStorageMb] = useState("0");

	const portalSubRoleOptions = useMemo(() => {
		const cur = String(detailUser?.role || "").trim();
		const base = [...PORTAL_SUB_EDIT_ROLES];
		if (cur && !base.includes(cur)) return [cur, ...base];
		return base;
	}, [detailUser?.role]);

	useEffect(() => {
		if (!isAdmin) return;
		let cancelled = false;
		getPlansAdmin()
			.then((res) => {
				if (cancelled) return;
				if (res?.status === 200 && Array.isArray(res.data)) setPlans(res.data);
			})
			.catch(() => {});
		return () => { cancelled = true; };
	}, [isAdmin]);

	const planByMongoId = useMemo(() => {
		const m = new Map();
		for (const p of plans || []) m.set(String(p?._id || ""), p);
		return m;
	}, [plans]);

	const planByStableId = useMemo(() => {
		const m = new Map();
		for (const p of plans || []) {
			const sid = String(p?.id || "").trim();
			if (sid) m.set(sid, p);
		}
		return m;
	}, [plans]);

	const resolvePlanForRole = useCallback(
		(role) => {
			const r = String(role || "").trim();
			if (!r) return null;
			const byKey = planByStableId.get(r) || planByMongoId.get(r);
			if (byKey) return byKey;
			const lower = r.toLowerCase();
			for (const p of plans || []) {
				if (String(p?.name || "").trim().toLowerCase() === lower) return p;
			}
			return null;
		},
		[planByStableId, planByMongoId, plans]
	);

	const getPlanAmountCents = useCallback(
		(role) => {
			const p = resolvePlanForRole(role);
			const cents = parseInt(String(p?.amountCents ?? "0"), 10);
			return Number.isFinite(cents) ? cents : 0;
		},
		[resolvePlanForRole]
	);

	const sortedRows = useMemo(() => {
		const list = Array.isArray(rows) ? [...rows] : [];
		list.sort((a, b) => getPlanAmountCents(b?.role) - getPlanAmountCents(a?.role));
		return list;
	}, [rows, getPlanAmountCents]);

	const load = useCallback(async (searchEmail) => {
		if (!isAdmin) return;
		setLoading(true);
		try {
			const res = await getPortalCustomers({ email: searchEmail || undefined, limit: 250 });
			if (res.status === 200 && Array.isArray(res.data)) setRows(res.data);
			else setRows([]);
		} catch (error) {
			setRows([]);
			alertError({ error, customMessage: "No se pudieron cargar los usuarios del portal." });
		} finally {
			setLoading(false);
		}
	}, [isAdmin]);

	useEffect(() => {
		load("");
	}, [load]);

	const onSearch = (e) => {
		e.preventDefault();
		load(email);
	};

	const openDetail = async (id) => {
		setDetailOpen(true);
		setDetailLoading(true);
		setDetailUser(null);
		setDetailEscapeRooms([]);
		setDetailMedia(null);
		setSubRoleDraft("");
		setSubNextDraft("");
		setSubAddonRooms("0");
		setSubAddonPlayers("0");
		setSubAddonStorageMb("0");
		try {
			const [uRes, erRes, mediaRes] = await Promise.all([
				getPortalCustomerById(id),
				getEscapeRoomsByUser(id).catch(() => ({ status: 200, data: [] })),
				getUserMediaByUserId(id).catch(() => ({ status: 200, data: { items: [], quota: null } })),
			]);
			if (uRes.status === 200 && uRes.data) {
				const u = uRes.data;
				const bill = resolveBillingSummary(u);
				setDetailUser(u);
				setSubRoleDraft(String(u.role || ""));
				setSubNextDraft(toDatetimeLocalValue(bill.nextAt));
				setSubNextDirty(false);
				setSubAddonRooms(String(Math.max(0, Math.floor(Number(u.planAddonExtraOwnedEscapeRooms) || 0))));
				setSubAddonPlayers(String(Math.max(0, Math.floor(Number(u.planAddonExtraMaxConcurrentPlayers) || 0))));
				setSubAddonStorageMb(String(Math.max(0, Math.floor(Number(u.planAddonExtraStorageMb) || 0))));
			}
			if (erRes?.status === 200 && Array.isArray(erRes.data)) setDetailEscapeRooms(erRes.data);
			if (mediaRes?.status === 200) setDetailMedia(mediaRes.data || null);
		} catch (error) {
			alertError({ error, customMessage: "No se pudo cargar el detalle." });
			setDetailOpen(false);
		} finally {
			setDetailLoading(false);
		}
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const userId = String(params.get("userId") || "").trim();
		if (!userId || !isAdmin) return;
		openDetail(userId);
	}, [location.search, isAdmin]);

	const savePortalSubscription = async () => {
		const userId = String(detailUser?._id || detailUser?.id || "").trim();
		if (!userId || subSaving) return;
		const bill = resolveBillingSummary(detailUser);
		const trimmedNext = String(subNextDraft || "").trim();
		let nextPayload = null;
		if (subNextDirty) {
			if (trimmedNext) {
				const d = new Date(trimmedNext);
				if (Number.isNaN(d.getTime())) {
					alertError({ customMessage: "La fecha y hora del próximo cargo no son válidas." });
					return;
				}
				nextPayload = d.toISOString();
			}
		}
		setSubSaving(true);
		try {
			const body = {
				role: String(subRoleDraft || "").trim(),
				planAddonExtraOwnedEscapeRooms: Math.max(0, Math.floor(Number(subAddonRooms) || 0)),
				planAddonExtraMaxConcurrentPlayers: Math.max(0, Math.floor(Number(subAddonPlayers) || 0)),
				planAddonExtraStorageMb: Math.max(0, Math.floor(Number(subAddonStorageMb) || 0)),
			};
			if (subNextDirty) {
				const nextKey = bill.isStripe ? "billingNextChargeAt" : "redsysNextChargeAt";
				body[nextKey] = nextPayload;
			}
			const res = await patchPortalCustomerSubscription(userId, body);
			if (res.status === 200 && res.data) {
				const u = res.data;
				const bill = resolveBillingSummary(u);
				setDetailUser(u);
				setSubRoleDraft(String(u.role || ""));
				setSubNextDraft(toDatetimeLocalValue(bill.nextAt));
				setSubNextDirty(false);
				setSubAddonRooms(String(Math.max(0, Math.floor(Number(u.planAddonExtraOwnedEscapeRooms) || 0))));
				setSubAddonPlayers(String(Math.max(0, Math.floor(Number(u.planAddonExtraMaxConcurrentPlayers) || 0))));
				setSubAddonStorageMb(String(Math.max(0, Math.floor(Number(u.planAddonExtraStorageMb) || 0))));
				alertSuccess({ customMessage: "Suscripción actualizada correctamente." });
			} else {
				alertError({
					customMessage: res?.data?.message || "Respuesta inválida del servidor al guardar.",
				});
			}
		} catch (error) {
			const msg = error?.response?.data?.message || "No se pudo guardar la suscripción.";
			alertError({ error, customMessage: msg });
		} finally {
			setSubSaving(false);
		}
	};

	const syncStripeBilling = async () => {
		const userId = String(detailUser?._id || detailUser?.id || "").trim();
		if (!userId || subSyncing) return;
		setSubSyncing(true);
		try {
			const res = await syncPortalCustomerStripeBilling(userId);
			if (res.status === 200 && res.data) {
				const u = res.data;
				const bill = resolveBillingSummary(u);
				setDetailUser(u);
				setSubNextDraft(toDatetimeLocalValue(bill.nextAt));
				setSubNextDirty(false);
				alertSuccess({ customMessage: "Estado de Stripe sincronizado." });
			} else {
				alertError({ customMessage: res?.data?.message || "No se pudo sincronizar." });
			}
		} catch (error) {
			alertError({ error, customMessage: error?.response?.data?.message || "No se pudo sincronizar con Stripe." });
		} finally {
			setSubSyncing(false);
		}
	};

	if (!isAdmin) {
		return (
			<Card>
				<CardHeader title="Usuarios del portal" />
				<CardBody>
					<Typography color="textSecondary">
						Solo los administradores pueden consultar el listado de usuarios del portal.
					</Typography>
				</CardBody>
			</Card>
		);
	}

	return (
		<>
			<Card>
				<CardHeader title="Usuarios del portal" />
				<CardBody>
					<Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
						Clientes y cuentas del portal. No se incluyen administradores ni moderadores (excepto la cuenta operativa admin@demo.com).
					</Typography>
					<form
						onSubmit={onSearch}
						style={{
							display: "flex",
							gap: 8,
							alignItems: "center",
							flexWrap: "wrap",
							marginBottom: 16,
						}}
					>
						<TextField
							size="small"
							variant="outlined"
							label="Correo"
							placeholder="filtrar por email"
							value={email}
							onChange={(ev) => setEmail(ev.target.value)}
							style={{ minWidth: 260 }}
						/>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							startIcon={<SearchIcon />}
							disabled={loading}
						>
							Buscar
						</Button>
						<Button
							type="button"
							variant="outlined"
							disabled={loading}
							onClick={() => {
								setEmail("");
								load("");
							}}
						>
							Ver todos
						</Button>
					</form>

					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress />
						</div>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Nombre</TableCell>
										<TableCell>Email</TableCell>
										<TableCell>Empresa</TableCell>
										<TableCell>Rol / plan</TableCell>
										<TableCell>Alta</TableCell>
										<TableCell align="right">Detalle</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{sortedRows.length === 0 ? (
										<TableRow>
											<TableCell colSpan={6} align="center">
												No hay resultados.
											</TableCell>
										</TableRow>
									) : (
										sortedRows.map((row) => {
											const plan = resolvePlanForRole(row?.role);
											const amountCents = getPlanAmountCents(row?.role);
											const priceLabel = formatPlanPriceParen(plan);
											const highlight = amountCents >= 2000;
											return (
											<TableRow key={row._id} style={highlight ? { background: "rgba(255, 247, 237, 0.8)" } : undefined}>
												<TableCell>{row.fullName || "—"}</TableCell>
												<TableCell>{row.email || "—"}</TableCell>
												<TableCell>{row.companyName || "—"}</TableCell>
												<TableCell>
													{row.role || "—"}
													{amountCents > 0 && priceLabel !== "—" ? (
														<span style={{ marginLeft: 8, fontSize: 12, fontWeight: 800, color: "#9a3412" }}>
															· {priceLabel}
														</span>
													) : null}
												</TableCell>
												<TableCell>{formatDate(row.createdAt)}</TableCell>
												<TableCell align="right">
													<Button
														size="small"
														startIcon={<VisibilityIcon />}
														onClick={() => openDetail(row._id)}
													>
														Ver
													</Button>
												</TableCell>
											</TableRow>
											);
										})
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardBody>
			</Card>

			<Dialog
				open={detailOpen}
				onClose={() => setDetailOpen(false)}
				fullScreen
				PaperProps={{ style: { background: "#f8fafc" } }}
			>
				<DialogTitle
					style={{
						background: "#f8fafc",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 12,
						paddingBottom: 8,
					}}
				>
					<span style={{ fontWeight: 800, fontSize: "1.2rem", color: "#0f172a" }}>Detalle del usuario</span>
					<Button variant="contained" color="primary" size="medium" onClick={() => setDetailOpen(false)} style={{ minWidth: 128, fontWeight: 700 }}>
						Cerrar
					</Button>
				</DialogTitle>
				<DialogContent style={{ background: "#f8fafc", paddingTop: 8, paddingBottom: 28 }}>
					{detailLoading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress />
						</div>
					) : detailUser ? (() => {
						const plan = resolvePlanForRole(detailUser?.role);
						const planLine =
							`${plan?.name || detailUser.role || "—"} (${formatPlanPriceParen(plan)})`;
						const quota = detailMedia?.quota || null;
						const used = Number(quota?.usedBytes || 0);
						const limit = Number(quota?.limitBytes || 0);
						const hasLimit = Number.isFinite(limit) && limit > 0;
						const pct = hasLimit ? Math.max(0, Math.min(100, Math.round((used / limit) * 100))) : 0;
						const items = Array.isArray(detailMedia?.items) ? detailMedia.items : [];
						return (
							<div style={{ maxWidth: 1500, margin: "0 auto", padding: "0 8px" }}>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
										gap: 18,
										alignItems: "stretch",
									}}
								>
									<SectionCard title="Info de la cuenta" icon={<AccountCircleIcon />}>
										<DetailRow label="Nombre" value={detailUser.fullName} />
										<DetailRow label="Email" value={detailUser.email} />
										<DetailRow label="Empresa" value={detailUser.companyName} />
										<DetailRow label="Plan" value={planLine} />
										<DetailRow label="Alta" value={formatDate(detailUser.createdAt)} />
										<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 10, wordBreak: "break-all" }}>
											ID: {detailUser._id}
										</Typography>
									</SectionCard>

									<SectionCard
										title="Escape rooms"
										icon={<MeetingRoomIcon />}
										right={detailEscapeRooms.length ? `${detailEscapeRooms.length}` : ""}
									>
										{detailEscapeRooms.length ? (
											<div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
												{detailEscapeRooms.slice(0, 9).map((er) => {
													const prev = escapeRoomPreviewUrl(er);
													return (
														<div
															key={er._id}
															style={{
																border: "1px solid #e5e7eb",
																borderRadius: 16,
																overflow: "hidden",
																background: "#fff",
																display: "flex",
																flexDirection: "column",
															}}
														>
															<div style={{ height: 108, background: "#0b1220", position: "relative" }}>
																{prev ? (
																	<img
																		src={prev}
																		alt=""
																		style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
																	/>
																) : (
																	<div
																		style={{
																			width: "100%",
																			height: "100%",
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "center",
																			color: "#94a3b8",
																			fontWeight: 800,
																			fontSize: 28,
																		}}
																	>
																		{(er.name || "?").slice(0, 1).toUpperCase()}
																	</div>
																)}
															</div>
															<div style={{ padding: 12, flex: 1, display: "flex", flexDirection: "column" }}>
																<div
																	style={{
																		fontWeight: 950,
																		color: "#0f172a",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																		fontSize: 14,
																	}}
																>
																	{er.name || "Escape Room"}
																</div>
																<div style={{ marginTop: 6, fontSize: 12, fontWeight: 800, color: "#64748b" }}>
																	{formatDate(er.createdAt)}
																</div>
																<div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
																	<Button
																		size="small"
																		color="primary"
																		variant="contained"
																		onClick={() => {
																			const path = `${publicBase}/escape-rooms/${er._id}`;
																			window.open(path, "_blank", "noopener,noreferrer");
																		}}
																	>
																		Editar
																	</Button>
																</div>
															</div>
														</div>
													);
												})}
											</div>
										) : (
											<div style={{ fontSize: 13, color: "#64748b" }}>—</div>
										)}
										{detailEscapeRooms.length > 9 ? (
											<div style={{ marginTop: 10, fontSize: 12, fontWeight: 800, color: "#64748b" }}>
												Mostrando 9 de {detailEscapeRooms.length}
											</div>
										) : null}
									</SectionCard>

									<SectionCard title="Plan y facturación" icon={<ReceiptIcon />}>
										{(() => {
											const bill = resolveBillingSummary(detailUser);
											return (
												<>
													<DetailRow label="Proveedor" value={bill.provider} />
													{bill.isStripe && bill.subId ? (
														<DetailRow label="Suscripción Stripe" value={bill.subId} />
													) : null}
													<DetailRow label="Último cargo" value={formatDate(bill.lastAt)} />
													<DetailRow label="Próximo cargo" value={formatDate(bill.nextAt)} />
													<DetailRow label="Estado último cargo" value={bill.status} />
													<Typography variant="subtitle2" style={{ marginTop: 14, marginBottom: 6, fontWeight: 800, color: "#0f172a" }}>
														Ajuste administrativo
													</Typography>
													<Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 10 }}>
														Queda registrado en Logs de planes.{" "}
														{bill.isStripe
															? "Stripe cobra en su servidor a la hora del periodo; nuestra BD se actualiza con el webhook o al sincronizar. Para forzar cobro ahora: pon la fecha a ahora y guarda."
															: "Con Redsys: la fecha programada dispara el cron de cobros recurrentes."}
													</Typography>
													{bill.isStripe ? (
														<Button
															size="small"
															color="primary"
															variant="outlined"
															onClick={syncStripeBilling}
															disabled={subSyncing}
															style={{ marginBottom: 12 }}
														>
															{subSyncing ? <CircularProgress size={18} /> : "Sincronizar con Stripe"}
														</Button>
													) : null}
												</>
											);
										})()}
										<FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
											<InputLabel id="portal-sub-role-label">Plan / rol</InputLabel>
											<Select
												labelId="portal-sub-role-label"
												label="Plan / rol"
												value={subRoleDraft}
												onChange={(e) => setSubRoleDraft(String(e.target.value))}
											>
												{portalSubRoleOptions.map((r) => {
													const p = resolvePlanForRole(r);
													const lab = p?.name ? String(p.name) : r;
													return (
														<MenuItem key={r} value={r}>
															{lab}
														</MenuItem>
													);
												})}
											</Select>
										</FormControl>
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											label="Próximo cargo (recurrente)"
											type="datetime-local"
											value={subNextDraft}
											onChange={(e) => {
												setSubNextDraft(e.target.value);
												setSubNextDirty(true);
											}}
											InputLabelProps={{ shrink: true }}
											style={{ marginBottom: 8 }}
										/>
										<Button
											size="small"
											color="default"
											variant="text"
											onClick={() => {
												setSubNextDraft("");
												setSubNextDirty(true);
											}}
											style={{ marginBottom: 8 }}
										>
											Dejar sin próximo cargo
										</Button>
										<Typography variant="subtitle2" style={{ marginTop: 14, marginBottom: 6, fontWeight: 800, color: "#0f172a" }}>
											Extras de plan (suman al límite del plan)
										</Typography>
										<Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
											Escape rooms extra, jugadores concurrentes extra por partida y megabytes de almacenamiento de medios extra.
										</Typography>
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											label="Escape rooms extra"
											type="number"
											inputProps={{ min: 0, max: 999999 }}
											value={subAddonRooms}
											onChange={(e) => setSubAddonRooms(e.target.value)}
											style={{ marginBottom: 10 }}
										/>
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											label="Jugadores por partida (extra)"
											type="number"
											inputProps={{ min: 0, max: 999999 }}
											value={subAddonPlayers}
											onChange={(e) => setSubAddonPlayers(e.target.value)}
											style={{ marginBottom: 10 }}
										/>
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											label="Almacenamiento extra (MB)"
											type="number"
											inputProps={{ min: 0, max: 999999 }}
											value={subAddonStorageMb}
											onChange={(e) => setSubAddonStorageMb(e.target.value)}
											style={{ marginBottom: 8 }}
										/>
										<div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
											<Button
												type="button"
												variant="contained"
												color="primary"
												size="small"
												disabled={subSaving}
												onClick={savePortalSubscription}
												style={{ minWidth: 160 }}
											>
												{subSaving ? <CircularProgress size={22} color="inherit" /> : "Guardar cambios"}
											</Button>
										</div>
										<div style={{ marginTop: 16 }}>
											<Button
												size="small"
												variant="outlined"
												color="primary"
												startIcon={<HistoryIcon />}
												onClick={() => {
													const em = String(detailUser?.email || "").trim();
													setDetailOpen(false);
													history.push(em ? `/plan-logs?email=${encodeURIComponent(em)}` : "/plan-logs");
												}}
											>
												Ver historial
											</Button>
										</div>
									</SectionCard>

									<SectionCard
										title="Multimedia"
										icon={<PermMediaIcon />}
										right={items.length ? `${items.length} archivos` : ""}
									>
										{quota ? (
											<div style={{ marginBottom: 12 }}>
												<div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, fontWeight: 900, color: "#64748b" }}>
													<span>{formatBytes(used)} usados</span>
													<span>{hasLimit ? `${formatBytes(limit)} límite` : "Ilimitado"}</span>
												</div>
												<div style={{ marginTop: 8, height: 10, borderRadius: 999, background: "#e2e8f0", overflow: "hidden" }}>
													<div style={{ height: "100%", width: `${pct}%`, background: pct >= 100 ? "#dc2626" : "#2563eb" }} />
												</div>
											</div>
										) : null}

										{items.length ? (
											<div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 12 }}>
												{items.slice(0, 24).map((it) => {
													let p = String(it.path || "");
													while (p.startsWith("/")) p = p.slice(1);
													const base = String(SERVER_URL || "").replace(/\/$/, "");
													const src = p.startsWith("http") ? p : `${base}/${p}`;
													return (
														<div key={it.name} style={{ border: "1px solid #e5e7eb", borderRadius: 18, overflow: "hidden", background: "#fff" }}>
															<div style={{ height: 110, background: "#0b1220" }}>
																{it.isVideo ? (
																	<video src={src} muted controls style={{ width: "100%", height: "100%", objectFit: "cover" }} />
																) : (
																	<img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
																)}
															</div>
															<div style={{ padding: 10, fontSize: 12, fontWeight: 800, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																{it.name}
															</div>
														</div>
													);
												})}
											</div>
										) : (
											<div style={{ fontSize: 13, color: "#64748b" }}>—</div>
										)}
									</SectionCard>
								</div>
							</div>
						);
					})() : null}
				</DialogContent>
			</Dialog>
		</>
	);
}
